import React, { Suspense } from 'react';
import ScrollWrapper from '@components/Atoms/ScrollWrapper';
import Box from '@components/Atoms/Box';
import ShowIf from '@components/Atoms/ShowIf';
import Skeleton from '@components/Atoms/Skeleton';
import Typography from '@components/Atoms/Typography';
import InformationCard from '@components/Component/InformationCard';
import DistrictReport from './DistrictReport';
import { useDistrictReport } from './hooks';
import { extractStudentNames } from './utils';
import { Section, DistrictReportSubjectData } from './types';

export type ReportType = 'skill' | 'lesson' | 'standard';

export type DistrictReportVariantConfig = {
  reportType: ReportType;
  loadingMessage: string;
  errorTitle: string;
  emptyTitle: string;
  emptyDescription: string;
  extractItems: (
    section: Section,
  ) =>
    | Array<{ students?: Array<{ student_id?: string; student_name?: string }> }>
    | undefined;
  SectionReport: React.ComponentType<{
    data: { id: string | undefined; examId: number | undefined; sectionId: string; hideOutcomeSlider: boolean };
    studentNameMap: Record<string, string>;
  }>;
};

type Props = {
  data?: DistrictReportSubjectData;
  districtId?: number;
  config: DistrictReportVariantConfig;
};

const BaseDistrictReport = ({ data, districtId: propDistrictId, config }: Props) => {
  const districtId = propDistrictId;
  const subjectId = data?.id;
  const examId = data?.examId;

  const {
    data: reportData,
    isLoading,
    error,
    status,
  } = useDistrictReport(districtId, examId, subjectId, config.reportType);

  const missingIds: string[] = [];
  if (districtId == null) missingIds.push('district');
  if (!subjectId || (typeof subjectId === 'string' && subjectId.trim() === ''))
    missingIds.push('subject');
  if (missingIds.length > 0) {
    return (
      <ScrollWrapper sx={{ height: '100%', width: '100%', minHeight: '37.5rem' }}>
        <InformationCard
          img="/images/500_error.svg"
          title={config.errorTitle}
          description={
            <>
              Cannot load report. Missing required parameters:{' '}
              {missingIds.join(', ')}. Please select a district and subject.
              <br />
              <br />
              Please try refreshing the page or selecting a different subject.
            </>
          }
        />
      </ScrollWrapper>
    );
  }

  if (!data) {
    return (
      <ScrollWrapper sx={{ height: '100%', width: '100%', minHeight: '37.5rem' }}>
        <InformationCard
          img="/images/no-content.png"
          title={config.emptyTitle}
          description={config.emptyDescription}
        />
      </ScrollWrapper>
    );
  }

  const {
    loadingMessage,
    errorTitle,
    emptyTitle,
    emptyDescription,
    extractItems,
    SectionReport,
  } = config;

  const hasOrganizations =
    !!reportData?.organizations && reportData.organizations.length > 0;
  const isCompleted = status === 'completed';
  const isProcessing = status === 'processing';
  const showEmptyState = isCompleted && !hasOrganizations;
  const showReport = hasOrganizations && (isCompleted || isProcessing);

  return (
    <ScrollWrapper
      sx={{ height: '100%', width: '100%', minHeight: '37.5rem' }}
      key={data?.id}
    >
      <ShowIf
        If={
          isLoading &&
          status !== 'completed' &&
          status !== 'failed' &&
          (status !== 'processing' || !reportData)
        }
      >
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6">{loadingMessage}</Typography>
          <Skeleton height="20rem" width="100%" />
        </Box>
      </ShowIf>

      <ShowIf If={!!error || status === 'failed'}>
        <InformationCard
          img="/images/500_error.svg"
          title={errorTitle}
          description={
            <>
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
              <br />
              <br />
              Please try refreshing the page or selecting a different subject.
            </>
          }
        />
      </ShowIf>

      <ShowIf If={showEmptyState}>
        <InformationCard
          img="/images/no-content.png"
          title={emptyTitle}
          description={emptyDescription}
        />
      </ShowIf>

      <ShowIf If={showReport}>
        <DistrictReport
          data={reportData!}
          sectionReport={(section) => {
            const studentNameMap = extractStudentNames(extractItems(section));

            return (
              <Suspense
                fallback={
                  <Box sx={{ p: 2 }}>
                    <Skeleton height="200px" width="100%" />
                  </Box>
                }
              >
                <SectionReport
                  data={{
                    id: data?.id,
                    examId: examId,
                    sectionId: section.section_id,
                    hideOutcomeSlider: true,
                  }}
                  studentNameMap={studentNameMap}
                />
              </Suspense>
            );
          }}
        />
      </ShowIf>
    </ScrollWrapper>
  );
};

export default BaseDistrictReport;
